import type { ParsedPoint } from '@/lib/parse';

/**
 * The ADS-B aggregators' shared trace format, and the one hard problem in it.
 *
 * adsb.lol, airplanes.live and the other readsb-derived globes all publish the
 * same per-aircraft, per-day JSON: a base timestamp and an array of positional
 * rows keyed by seconds after it. That is convenient — one small file is both
 * the search result and the track — and it carries one complication no other
 * source in this app has.
 *
 * **A day file is a day, not a flight.** `ARCHITECTURE.md` §6 already names
 * this under "multi-flight files": an FR24 export is one flight, but a file
 * covering a whole day of circuits holds eight. Splitting them is the point of
 * this module, and the rules are deliberately dull and inspectable rather than
 * clever:
 *
 *  - a gap in coverage longer than `TRACE_GAP_MIN` minutes ends a flight,
 *    because an aircraft parked on the ramp simply stops being seen; and
 *  - a continuous run on the ground longer than `GROUND_SPLIT_MIN` minutes
 *    ends one too, which is what separates two sorties from a long backtrack.
 *    A touch-and-go leaves the ground for seconds and stays one flight, which
 *    is the behaviour a circuit session needs.
 *
 * Both thresholds are wrong for somebody. They are exported and stated here so
 * the guess is visible, and everything they produce still arrives in the
 * logbook as a draft a pilot approves.
 */

/** A coverage gap this long ends a flight. */
export const TRACE_GAP_MIN = 30;

/** A continuous run on the ground this long ends a flight. */
export const GROUND_SPLIT_MIN = 10;

/** Below this many fixes, a segment is a fragment of coverage rather than a flight. */
export const MIN_SEGMENT_FIXES = 5;

/**
 * One row of `trace`. Positional, sparse, and full of nulls:
 * `[secondsAfterBase, lat, lon, altFtOrGround, gsKt, trackDeg, flags, vsFpm, ...]`.
 */
export type TraceRow = readonly unknown[];

export interface AircraftTrace {
  /** ICAO 24-bit address, lowercase hex. */
  icao?: string;
  /** Registration, as the aggregator holds it. */
  r?: string;
  /** ICAO type designator. */
  t?: string;
  desc?: string;
  /** Epoch seconds every row's offset is relative to. */
  timestamp?: number;
  trace?: TraceRow[];
}

/** A fix, plus the one fact the splitter needs that a `ParsedPoint` has no room for. */
export interface TraceFix extends ParsedPoint {
  onGround: boolean;
}

/**
 * `Array.isArray` narrows a `readonly unknown[]` to `any[]`, which quietly
 * turns every element read below into an `any`. A hand-written guard keeps the
 * elements `unknown`, which is what they actually are.
 */
function isRow(value: unknown): value is readonly unknown[] {
  return Array.isArray(value);
}

function numberOrNaN(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.NaN;
}

/**
 * Rows to fixes.
 *
 * The altitude column is the interesting one: readsb writes the literal string
 * `"ground"` there rather than a number when the aircraft is on the surface. A
 * plain `Number()` turns that into `NaN` and loses the one signal the splitter
 * runs on, so it is read as ground *and* as an altitude of zero — which is what
 * it means.
 */
export function traceFixes(trace: AircraftTrace): TraceFix[] {
  const baseS = numberOrNaN(trace.timestamp);
  if (!Number.isFinite(baseS)) {
    return [];
  }

  const fixes: TraceFix[] = [];
  for (const row of trace.trace ?? []) {
    if (!isRow(row)) {
      continue;
    }
    const offsetS = numberOrNaN(row[0]);
    const lat = numberOrNaN(row[1]);
    const lon = numberOrNaN(row[2]);
    if (!Number.isFinite(offsetS) || !Number.isFinite(lat) || !Number.isFinite(lon)) {
      continue;
    }
    const rawAlt = row[3];
    const onGround = rawAlt === 'ground';
    fixes.push({
      tMs: (baseS + offsetS) * 1000,
      lat,
      lon,
      altFt: onGround ? 0 : numberOrNaN(rawAlt),
      gsKt: numberOrNaN(row[4]),
      trackDeg: numberOrNaN(row[5]),
      onGround,
    });
  }
  fixes.sort((a, b) => a.tMs - b.tMs);
  return fixes;
}

/**
 * One day's fixes, split into the flights they describe.
 *
 * @returns each flight's fixes, in order, dropping fragments too short to be
 *   one and runs that never left the ground.
 */
export function splitTraceIntoFlights(
  fixes: readonly TraceFix[],
  options: { gapMin?: number; groundSplitMin?: number } = {},
): TraceFix[][] {
  const gapMs = (options.gapMin ?? TRACE_GAP_MIN) * 60_000;
  const groundMs = (options.groundSplitMin ?? GROUND_SPLIT_MIN) * 60_000;

  const segments: TraceFix[][] = [];
  let current: TraceFix[] = [];
  /** Index within `current` where the run of on-ground fixes began, or -1. */
  let groundRunStart = -1;

  const flush = (): void => {
    if (current.length > 0) {
      segments.push(current);
      current = [];
    }
    groundRunStart = -1;
  };

  for (const fix of fixes) {
    const previous = current[current.length - 1];

    if (previous !== undefined && fix.tMs - previous.tMs > gapMs) {
      flush();
    }

    current.push(fix);

    if (!fix.onGround) {
      groundRunStart = -1;
      continue;
    }

    if (groundRunStart === -1) {
      groundRunStart = current.length - 1;
      continue;
    }

    const runStart = current[groundRunStart];
    if (runStart !== undefined && fix.tMs - runStart.tMs > groundMs) {
      /*
       * Cut inside the ground run rather than at its edge, keeping the fix
       * that started it on the flight that just ended and the current fix on
       * the one about to begin. Both flights then still show the aeroplane
       * reaching and leaving the ground, which is what the landing detector
       * downstream looks for.
       */
      const tail = current.slice(0, groundRunStart + 1);
      segments.push(tail);
      current = [fix];
      groundRunStart = 0;
    }
  }
  flush();

  return segments.filter(
    (segment) => segment.length >= MIN_SEGMENT_FIXES && segment.some((fix) => !fix.onGround),
  );
}
